import * as React from "react";
import { format } from "date-fns";
import { et } from "date-fns/locale";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { DateTimePanel } from "./date-time-panel";
import { Button } from "./button";
import { cn } from "../lib/utils";

interface DateTimePickerProps {
  /** Full ISO datetime string (e.g. `2026-06-12T14:30:00.000Z`). Empty = unset. */
  value?:       string;
  onChange:     (v: string) => void;
  placeholder?: string;
  disabled?:    boolean;
  /** Display locale ("en" | "et"). Affects label format + calendar week-start. */
  locale?:      string;
  className?:   string;
  clearable?:   boolean;
  /** Accessible name of the clear (x) control. Default English "clear". */
  clearLabel?:  string;
  /**
   * Turn the month caption into month + year dropdowns. See `DateTimePanel`,
   * which owns the behaviour; this only forwards it.
   */
  withYearPicker?: boolean;
}

/**
 * Date + time picker - shadcn/Radix Popover wrapping the shared `DateTimePanel`
 * (react-day-picker calendar + HH/MM inputs). Outputs an ISO datetime; consumers store
 * it as a string alongside other form fields.
 *
 * For a field on an ordinary form. Inside a dialog, embed `DateTimePanel` directly -
 * the calendar is then visible at once instead of behind a trigger in an already-open layer.
 */
export function DateTimePicker({
  value, onChange, placeholder, disabled, locale = "et", className, clearable = true,
  clearLabel = "clear", withYearPicker,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  // Accept any Estonian locale tag ("et", "et-EE", …) - callers pass either.
  const isEt = (locale ?? "").toLowerCase().startsWith("et");

  const date: Date | undefined = React.useMemo(() => {
    if (!value) return undefined;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  const label = date
    ? format(date, isEt ? "dd.MM.yyyy HH:mm" : "yyyy-MM-dd HH:mm", isEt ? { locale: et } : undefined)
    : "";

  const fallback = placeholder ?? (isEt ? "Vali kuupäev ja kellaaeg" : "Pick date and time");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "h-10 w-full inline-flex items-center justify-between gap-2 rounded-md border border-input bg-card px-3 text-sm shadow-soft",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
            "disabled:cursor-not-allowed disabled:opacity-50 hover:bg-accent/30 transition-colors",
            !label && "text-muted-foreground",
            className,
          )}
        >
          <span className="inline-flex items-center gap-2 truncate">
            <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
            <span className="truncate tabular-nums">{label || fallback}</span>
          </span>
          {clearable && label && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange(""); }}
              className="size-5 rounded-sm hover:bg-destructive/15 text-muted-foreground hover:text-destructive inline-flex items-center justify-center"
              aria-label={clearLabel}
            >
              <X className="size-3" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <DateTimePanel
          value={value}
          onChange={onChange}
          locale={locale}
          withYearPicker={withYearPicker}
          autoFocus
          footer={
            <Button type="button" size="sm" className="w-full" onClick={() => setOpen(false)}>
              {isEt ? "Valmis" : "Done"}
            </Button>
          }
        />
      </PopoverContent>
    </Popover>
  );
}
